/**
 * The microphone as DeepL Voice wants it: mono, 16-bit little-endian PCM at
 * 16 kHz, in chunks of 200 ms (DeepL recommends 50–250 ms).
 *
 * The AudioContext runs at the device's own rate and the worklet resamples:
 * Firefox refuses to connect a microphone to a context of another rate, and
 * MediaRecorder's containers differ between browsers (Safari has no WebM).
 */
export const PCM_SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = PCM_SAMPLE_RATE / 5;

/**
 * Runs on the audio thread. Linear interpolation is enough for speech: the
 * recogniser listens below 8 kHz anyway. `sampleRate` is the worklet scope's
 * global — the context's (device) rate.
 */
const WORKLET_SOURCE = `
class PcmChunker extends AudioWorkletProcessor {
    constructor() {
        super();
        this.step = sampleRate / ${PCM_SAMPLE_RATE};
        this.position = 0;
        this.previous = 0;
        this.chunk = new Int16Array(${CHUNK_SAMPLES});
        this.filled = 0;
        this.port.onmessage = (event) => {
            if (event.data !== 'flush') {
                return;
            }
            if (this.filled > 0) {
                const rest = this.chunk.slice(0, this.filled);
                this.port.postMessage(rest.buffer, [rest.buffer]);
                this.filled = 0;
            }
            this.port.postMessage('flushed');
        };
    }
    process(inputs) {
        const channel = inputs[0] && inputs[0][0];
        if (!channel) {
            return true;
        }
        while (this.position < channel.length) {
            const index = Math.floor(this.position);
            const fraction = this.position - index;
            const left = index === 0 ? this.previous : channel[index - 1];
            const right = channel[index];
            const value = left + (right - left) * fraction;
            const clamped = Math.max(-1, Math.min(1, value));
            this.chunk[this.filled++] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
            if (this.filled === this.chunk.length) {
                this.port.postMessage(this.chunk.buffer, [this.chunk.buffer]);
                this.chunk = new Int16Array(${CHUNK_SAMPLES});
                this.filled = 0;
            }
            this.position += this.step;
        }
        this.position -= channel.length;
        this.previous = channel[channel.length - 1];
        return true;
    }
}
registerProcessor('pcm-chunker', PcmChunker);
`;

export interface PcmRecorder {
    /** Release the microphone. Chunks already handed over stay handed over. */
    stop(): void;
    /**
     * Hand over what the worklet holds of the current chunk — the last words
     * before the click — then stop. Resolves once that has come through.
     */
    finish(): Promise<void>;
}

/** How long to wait for the worklet's last piece before stopping anyway. */
const FLUSH_TIMEOUT_MS = 400;

/** Why the microphone could not be opened, for the message to show. */
export class MicrophoneError extends Error {
    readonly reason: 'denied' | 'unavailable';

    constructor(reason: 'denied' | 'unavailable') {
        super(reason);
        this.reason = reason;
        this.name = 'MicrophoneError';
    }
}

/** Little-endian on every platform a browser runs on; DeepL wants s16le. */
export function chunkToBase64(chunk: ArrayBuffer): string {
    const bytes = new Uint8Array(chunk);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export async function startPcmRecorder(
    onChunk: (chunk: ArrayBuffer) => void
): Promise<PcmRecorder> {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw new MicrophoneError('unavailable');
    }
    let stream: MediaStream;
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true
            }
        });
    } catch (error) {
        throw new MicrophoneError(
            error instanceof DOMException && error.name === 'NotAllowedError'
                ? 'denied'
                : 'unavailable'
        );
    }
    const context = new AudioContext();
    // Opened after awaiting the session, a context can start suspended under
    // the autoplay rules; recording from a live microphone may resume it.
    await context.resume().catch(() => {});
    const moduleUrl = URL.createObjectURL(
        new Blob([WORKLET_SOURCE], { type: 'application/javascript' })
    );
    try {
        await context.audioWorklet.addModule(moduleUrl);
    } catch {
        stream.getTracks().forEach((track) => track.stop());
        void context.close();
        throw new MicrophoneError('unavailable');
    } finally {
        URL.revokeObjectURL(moduleUrl);
    }
    const source = context.createMediaStreamSource(stream);
    const chunker = new AudioWorkletNode(context, 'pcm-chunker');
    let onFlushed: (() => void) | null = null;
    chunker.port.onmessage = (event: MessageEvent<ArrayBuffer | 'flushed'>) => {
        if (event.data === 'flushed') {
            onFlushed?.();
            return;
        }
        onChunk(event.data);
    };
    source.connect(chunker);
    // Its output is silence; connected only because an engine that renders
    // the graph from the destination backwards (WebKit) skips a node that
    // leads nowhere, and no chunk would ever arrive.
    chunker.connect(context.destination);

    let stopped = false;
    function stop() {
        if (stopped) {
            return;
        }
        stopped = true;
        chunker.port.onmessage = null;
        source.disconnect();
        chunker.disconnect();
        stream.getTracks().forEach((track) => track.stop());
        void context.close();
    }

    return {
        stop,
        finish() {
            if (stopped) {
                return Promise.resolve();
            }
            return new Promise<void>((resolve) => {
                const done = () => {
                    window.clearTimeout(timer);
                    onFlushed = null;
                    stop();
                    resolve();
                };
                const timer = window.setTimeout(done, FLUSH_TIMEOUT_MS);
                onFlushed = done;
                chunker.port.postMessage('flush');
            });
        }
    };
}
